import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import type { AdminLearningService, CollectionName } from '../services/learningService';
import type { LearningSection, CreateSectionData } from '../services/learningTypes';
import ImagePicker from '../../../pickers/images/ImagePicker';
import Button from '../../../components/Button';
import { sanitizeId, getIdValidationError, cleanIdForStorage } from '../../../utils/idValidation';

interface SectionFormProps {
    section?: LearningSection | null;
    collection: CollectionName;
    service: AdminLearningService;
    onSave: () => void;
    onCancel: () => void;
}

export default function SectionForm({ section, collection, service, onSave, onCancel }: SectionFormProps) {
    const [formData, setFormData] = useState<CreateSectionData>({
        title: '',
        subtitle: '',
        imageSrc: '',
        status: 'draft',
        isSupportOnly: false,
        order: 1
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (section) {
            setFormData({
                id: section.uid,
                title: section.title,
                subtitle: section.subtitle,
                imageSrc: section.imageSrc,
                status: section.status,
                isSupportOnly: section.isSupportOnly,
                order: section.order
            });
        } else {
            // Get next order for new sections
            const getNextOrder = async () => {
                try {
                    const nextOrder = await service.getNextOrder(collection);
                    setFormData(prev => ({ ...prev, order: nextOrder }));
                } catch (err) {
                    console.error('Error getting next order:', err);
                }
            };
            getNextOrder();
        }
    }, [section, service, collection]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        // Basic validation
        if (!formData.title.trim()) {
            setError('Title is required');
            setLoading(false);
            return;
        }
        if (!formData.subtitle.trim()) {
            setError('Subtitle is required');
            setLoading(false);
            return;
        }

        try {
            // Clean the ID for storage
            const cleanedFormData = {
                ...formData,
                id: formData.id ? cleanIdForStorage(formData.id) : formData.id
            };

            if (section) {
                await service.updateSection(section.uid, cleanedFormData);
            } else {
                await service.createSection(cleanedFormData);
            }
            onSave();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save section');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field: keyof CreateSectionData, value: string | number | boolean) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    return (
        <div className="fixed inset-0 bg-gray-600/50 overflow-y-auto h-full w-full z-50 px-4 py-6 sm:py-20">
            <div className="relative mx-auto p-0 border w-full max-w-md shadow-lg rounded-md bg-white">
                {/* Header */}
                <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">
                        {section ? 'Edit Section' : 'Create New Section'}
                    </h3>
                    <Button
                        onClick={onCancel}
                        variant="ghost"
                        icon={<X size={24} />}
                    />
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-4 sm:p-6">
                    {error && (
                        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
                            <div className="text-sm text-red-700">{error}</div>
                        </div>
                    )}

                    <div className="space-y-4">
                        {/* Section ID */}
                        <div>
                            <label htmlFor="id" className="block text-sm font-medium text-gray-700 mb-1">
                                Section ID
                            </label>
                            <input
                                type="text"
                                id="id"
                                value={formData.id || ''}
                                onChange={(e) => handleChange('id', sanitizeId(e.target.value))}
                                placeholder="e.g., understanding-ocd"
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${getIdValidationError(formData.id || '') ? 'border-red-300' : 'border-gray-300'
                                    }`}
                            />
                            {getIdValidationError(formData.id || '') && (
                                <p className="text-xs text-red-600 mt-1">
                                    {getIdValidationError(formData.id || '')}
                                </p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                                Custom ID for the section. Leave empty for auto-generated ID. No spaces allowed - use hyphens instead.
                            </p>
                        </div>

                        {/* Title */}
                        <div>
                            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                                Title *
                            </label>
                            <input
                                type="text"
                                id="title"
                                value={formData.title}
                                onChange={(e) => handleChange('title', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="e.g., Understanding OCD"
                                required
                            />
                        </div>

                        {/* Subtitle */}
                        <div>
                            <label htmlFor="subtitle" className="block text-sm font-medium text-gray-700 mb-1">
                                Subtitle *
                            </label>
                            <input
                                type="text"
                                id="subtitle"
                                value={formData.subtitle}
                                onChange={(e) => handleChange('subtitle', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="e.g., Learn the fundamentals"
                                required
                            />
                        </div>

                        {/* Image Source */}
                        <ImagePicker
                            value={formData.imageSrc}
                            onChange={(url) => handleChange('imageSrc', url)}
                            folder="sections"
                            label="Section Image"
                            placeholder="No section image selected"
                        />

                        {/* Order */}
                        <div>
                            <label htmlFor="order" className="block text-sm font-medium text-gray-700 mb-1">
                                Order
                            </label>
                            <input
                                type="number"
                                id="order"
                                value={formData.order}
                                onChange={(e) => handleChange('order', parseInt(e.target.value) || 1)}
                                min="1"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        {/* Status */}
                        <div>
                            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                                Status
                            </label>
                            <select
                                id="status"
                                value={formData.status}
                                onChange={(e) => handleChange('status', e.target.value as 'draft' | 'published')}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="draft">Draft</option>
                                <option value="published">Published</option>
                            </select>
                        </div>

                        {/* Support Only */}
                        <div>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    id="isSupportOnly"
                                    checked={formData.isSupportOnly}
                                    onChange={(e) => handleChange('isSupportOnly', e.target.checked)}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <div>
                                    <span className="text-sm font-medium text-gray-700">Support Only</span>
                                    <p className="text-xs text-gray-500">
                                        This section contains lessons meant for quest support only
                                    </p>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-3 mt-6">
                        <Button
                            type="button"
                            onClick={onCancel}
                            variant="secondary"
                            fullWidth
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            fullWidth
                            loading={loading}
                            disabled={loading}
                        >
                            {loading ? 'Saving...' : (section ? 'Update' : 'Create')}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
} 